import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError } from '../api/client';
import {
  getStatistics,
  STATS_PRESETS,
  STATS_TAB_DAYS,
  type PAStats,
  type PAStatsDay,
} from '../api/statistics';
import type { PAAccount, PAContract } from '../api/money';
import { formatCivilDate, formatDuration, formatOctets, NO_VALUE } from '../format';
import { AccountScope } from '../money/AccountScope';
import { DataTable, FactList, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery, type PAQuery } from '../money/usePAQuery';
import { statsTabLabel } from '../money/labels';

/**
 * ============================================================================
 * «СТАТИСТИКА» — §14, F13 / F15, Appendix L §2.21
 * ============================================================================
 *
 * # ⚠ THE DEFECT THIS SCREEN IS BUILT NOT TO HAVE: SILENT ZEROS
 *
 * A detailed-store client with no configured DSN returns an EMPTY SERIES WITH A NIL ERROR
 * from every read. So a statistics page written the obvious way renders «0 Б, 0 сессий» on an
 * installation that cannot see its own traffic store — and a subscriber cannot tell that from
 * «вы ничего не скачали». **When `data_available` is false this screen renders NO FIGURE AT
 * ALL**, not a zero, not a dash in a totals row, not an empty table with a header: one
 * sentence saying we could not read the store. The three server behaviours are separated on
 * purpose and each gets its own rendering:
 *
 *   NOT CONFIGURED       → 200, `data_available: false`  → the sentence, and nothing numeric.
 *   CONFIGURED + FAILING → **500**                       → `QueryFrame`'s error frame with
 *                          «Повторить». Loud, which is the better failure: there is no honest
 *                          number to print.
 *   CONFIGURED + EMPTY   → 200, `data_available: true`   → «трафика не зафиксировано» AND the
 *                          zero totals, because that IS the truth and it is the only case
 *                          allowed to say so.
 *
 * # ⚠ AND A FOURTH STATE, WHICH IS WHAT THE CUTOVER WILL PRODUCE ALL DAY
 *
 * *(Added 2026-08-12, the second review fix pass, with `PAStats.data_since`.)* «The store
 * answered and held nothing» is TWO different facts wearing one body, and until this field
 * existed the screen could not tell them apart:
 *
 *   he used nothing            the store was collecting over that window and recorded no
 *                              traffic. «За выбранный период трафика не зафиксировано» — true,
 *                              and the case above.
 *   we were not collecting yet  the window ends before `data_since`, the first day this store
 *                              holds a row for him. The SAME empty body, and the same sentence
 *                              would be a claim ABOUT THE SUBSCRIBER assembled entirely out of
 *                              a fact about the SERVER — told, after cutover, to every
 *                              subscriber who asks for any pre-cutover month.
 *
 * So a window that STARTS before `data_since` carries «данные собираются с …», and a window
 * that ENDS before it renders NO FIGURE AT ALL — the same rule as `data_available: false` and
 * for the same reason, because every zero on such a page is zero for a server-side reason.
 *
 * ⚠ `data_since: ""` IS NOT A DATE and is never formatted as one. It means «we cannot tell you
 * when your history begins» (a new Account with no rows, or a store that could not be asked),
 * and the screen falls back to saying nothing about collection — `formatCivilDate('')` is «—»,
 * so a screen that skipped the check would print «данные собираются с —».
 *
 * # ⚠ A DAY WITH NO TRAFFIC IS ABSENT, NOT ZERO
 *
 * `days` carries one row per day WITH DATA. This screen renders the rows it was given and
 * fabricates none: a gap in the series is a day the store holds nothing for, and zero-filling
 * it would invent thirty rows of «0 Б» for a subscriber who was away. The sentence under the
 * table says so, because a reader counting rows would otherwise think days were missing.
 *
 * # ⚠ ONE TAB, AND THE COUNT IS A MEASUREMENT RATHER THAN A DECISION
 *
 * §14 draws four tabs — По дням / По часам / По категориям / По адресам. Three have no data
 * source in `backend/migrations/clickhouse/` at all: there is no hourly summary; the daily
 * summary's materialized view hard-codes `'total' AS traffic_category`, so «По категориям»
 * could only ever return one bucket; «По адресам» needs `netflow_flows`, which no Go read
 * function touches. Telephony statistics have no data path whatever. So the server publishes
 * `tabs` and **this screen renders exactly the intersection of that list with what it can
 * fill** — no disabled tab, no empty tab, no «в разработке». No operator toggle exists for the
 * three, so a greyed-out tab would be a control nobody can enable.
 *
 * # ⚠ THE PERIOD CONTROLS SIT OUTSIDE THE QUERY FRAME, AND THAT IS LOAD-BEARING
 *
 * The commonest refusal on this screen is `409 pa.period_out_of_retention` — he asked for a
 * period older than the store keeps. If the controls lived inside the frame the error would
 * replace them, and the only way out of the error would be the control the error had just
 * removed. So the chips and the two date boxes are always on screen, and the frame below them
 * carries the outcome.
 *
 * ⚠ AND IT IS NOW THREE REFUSALS THROUGH THAT ONE DOOR, not one: `400 pa.period_incomplete`
 * and `400 pa.period_reversed` join it there. *(Phase 7 W5, 2026-08-12.)* This screen used to
 * answer those two ITSELF, without asking, because both came back as `pa.invalid_period` —
 * the YYYY-MM month filter's code, telling a subscriber who left one of two DATE boxes empty
 * to type a month. `c5d9ab7` gave the surface its own vocabulary and the workaround was
 * deleted with it: a guard left standing beside a server that has been fixed is a second
 * source of truth, and the client's copy is the one nobody re-reads. See [applyRange] and
 * [blamedFields].
 *
 * ⚠ AND THE RETENTION FLOOR IS READ FROM THE REFUSAL AS WELL AS FROM THE ANSWER. `min` on the
 * date boxes is `earliest_date` — from the last successful read, or from `details.earliest_date`
 * on the 409 that named it. A period starting before it is REFUSED and never clamped, because a
 * clamped range renders as «вы ничего не потратили в январе 2019», which is a statement about
 * the subscriber instead of about the store.
 *
 * # WHAT IS DELIBERATELY NOT ON THIS SCREEN
 *
 *   - **THE THREE STRUCK TABS**, in any form, enabled or disabled. See above.
 *   - **A TELEPHONY VIEW.** D3 defers telephony rating and there is no CDR table anywhere, so
 *     §2.21's «Internet **and** Telephony» cannot be honoured for the second half.
 *   - **§14's «СПИСАНИЯ» COLUMN on the account picker.** It is billing charge rows and not
 *     ClickHouse — the same source as «Расход средств», which is its own section one press
 *     away. Reproducing it here would be a second answer about money on a traffic screen.
 *   - **A PAGE-SIZE CONTROL.** §2.21 makes page size and the default range operator-configurable
 *     and NEITHER SETTINGS KEY EXISTS; the read applies no LIMIT at all, so there is no page to
 *     size. A control here would be one nobody can configure and nothing would read.
 */
export function StatisticsScreen() {
  return (
    <MoneyPage titleKey="statistics.title" leadKey="statistics.lead">
      {(contract) => (
        <AccountScope
          contractID={contract.id}
          labelKey="statistics.accountLabel"
          emptyKey="statistics.noAccounts"
          selectID="pa-statistics-account"
        >
          {(account) => <StatisticsBody contract={contract} account={account} />}
        </AccountScope>
      )}
    </MoneyPage>
  );
}

/** The request's own period: a preset, OR an explicit inclusive range. Never both. */
interface Period {
  preset: string;
  from: string;
  to: string;
}

function StatisticsBody({ contract, account }: { contract: PAContract; account: PAAccount }) {
  const { t } = useTranslation();
  // S32's own default, and it is the SERVER's too — so the first read asks for exactly what
  // the server would have chosen, rather than for «whatever the browser thinks this month is».
  const [period, setPeriod] = useState<Period>({ preset: 'month', from: '', to: '' });
  const [fromText, setFromText] = useState('');
  const [toText, setToText] = useState('');

  const { query, reload } = usePAQuery<PAStats>(
    () =>
      getStatistics(contract.id, account.account_id, {
        preset: period.preset,
        from: period.from,
        to: period.to,
      }),
    [contract.id, account.account_id, period.preset, period.from, period.to],
  );

  const earliest = retentionFloor(query);
  const blamed = blamedFields(query);

  const choosePreset = (preset: string) => {
    setFromText('');
    setToText('');
    setPeriod({ preset, from: '', to: '' });
  };

  /*
    ⚠ THIS SENDS THE FORM AS IT STANDS AND JUDGES NOTHING. It used to hold two local
    completeness checks — «both ends required» and «`to` may not precede `from`» — which
    returned early and never asked the server at all.

    *(Removed 2026-08-12, Phase 7 W5, because the server-side defect they worked around was
    fixed in `c5d9ab7`.)* They existed for a measured reason: both refusals used to answer
    `pa.invalid_period`, whose Russian message is «Неверный период: ожидается месяц в формате
    ГГГГ-ММ» — written for the YYYY-MM month filter on «Расход средств», so a subscriber who
    left one of two DATE boxes empty was told to type a month. There are now two codes with
    their own wording and their own `details.field`: `pa.period_incomplete` and
    `pa.period_reversed`. A client-side copy of a rule the server now states correctly is a
    SECOND SOURCE OF TRUTH, and it drifts silently — the server is free to change what a
    period may be, and only the half nobody re-reads would keep enforcing the old rule.
  */
  const applyRange = () => {
    setPeriod({ preset: '', from: fromText.trim(), to: toText.trim() });
  };

  return (
    <>
      <Card className="pa-block" title={t('statistics.periodTitle')}>
        <div className="pa-chips">
          {STATS_PRESETS.map((preset) => (
            <Button
              key={preset}
              size="large"
              type={period.preset === preset ? 'primary' : 'default'}
              onClick={() => choosePreset(preset)}
            >
              {t(`statistics.preset.${preset}`)}
            </Button>
          ))}
        </div>

        <div className="pa-range">
          <div className="pa-range-field">
            <label className="pa-field-label" htmlFor="pa-stats-from">
              {t('statistics.fromLabel')}
            </label>
            <Input
              id="pa-stats-from"
              type="date"
              size="large"
              value={fromText}
              // ⚠ THE SERVER'S RETENTION FLOOR, never a browser-derived bound. See the header.
              min={earliest}
              // ⚠ THE SERVER NAMED THIS BOX. See [blamedFields].
              status={blamed.has('from') ? 'error' : undefined}
              onChange={(e) => setFromText(e.target.value)}
            />
          </div>
          <div className="pa-range-field">
            <label className="pa-field-label" htmlFor="pa-stats-to">
              {t('statistics.toLabel')}
            </label>
            <Input
              id="pa-stats-to"
              type="date"
              size="large"
              value={toText}
              min={earliest}
              status={blamed.has('to') ? 'error' : undefined}
              onChange={(e) => setToText(e.target.value)}
            />
          </div>
          <Button size="large" onClick={applyRange}>
            {t('statistics.show')}
          </Button>
        </div>
        {/* ⚠ NO LOCAL ERROR SENTENCE. The refusal is the SERVER's and it is rendered ONCE, by
            the `QueryFrame` below — the same place `pa.period_out_of_retention` has always
            appeared. A second sentence here would be a second wording of one verdict, and it
            is the wording that was wrong in the first place. */}
        {earliest ? (
          <p className="pa-field-hint">
            {t('statistics.retentionHint', { date: formatCivilDate(earliest) })}
          </p>
        ) : null}
      </Card>

      <QueryFrame query={query} reload={reload}>
        {(data) => <StatisticsData data={data} />}
      </QueryFrame>
    </>
  );
}

/**
 * retentionFloor is the earliest day the read will ACCEPT — from the ANSWER, or from the
 * REFUSAL that named it.
 *
 * ⚠ IT IS NOT «the earliest day the store holds», which is what this comment said until
 * 2026-08-12 and what the hint beside the boxes told the subscriber. The server computes it as
 * `today − EffectiveRetentionDays()`, floored at the tables' 1095-day TTL — a CEILING on the
 * question, computed from a configured duration, with nothing measured. The first day this
 * store actually holds anything is a different number and is not on the wire; see
 * `statistics.retentionHint` for why the portal must not guess it.
 *
 * ⚠ READING IT OFF THE 409 IS THE POINT. `pa.period_out_of_retention` carries
 * `details.earliest_date`, and adopting it means the box that produced the refusal immediately
 * stops offering the day that caused it. Without that the subscriber can retype the same
 * period for ever and the screen tells him the same thing each time.
 *
 * It is deliberately NOT held in state: the query already holds the last answer and the last
 * error, so a second copy would be a second answer about the same fact — and the copy would be
 * the stale one after an account switch.
 */
function retentionFloor(query: PAQuery<PAStats>): string {
  if (query.status === 'ready') return query.data.earliest_date;
  if (query.status === 'error' && query.error instanceof ApiError) {
    const details = (query.error.details ?? {}) as { earliest_date?: string };
    if (typeof details.earliest_date === 'string') return details.earliest_date;
  }
  return '';
}

/** The two date boxes, by the name the server uses for each. Nothing else may be marked. */
const PERIOD_FIELDS: ReadonlySet<string> = new Set(['from', 'to']);
const NO_FIELDS: ReadonlySet<string> = new Set();

/**
 * blamedFields names the date box (or boxes) the SERVER's last refusal held responsible,
 * read from `details.field` — «from,to» for `pa.period_incomplete`, «to» for
 * `pa.period_reversed`.
 *
 * ⚠ IT IS READ, NEVER RE-DERIVED, AND THAT IS THE WHOLE REASON THE FIELD EXISTS. Deciding
 * locally which box is at fault means re-implementing the period rule on the client — which
 * is exactly the workaround this screen carried until 2026-08-12 and exactly what makes such
 * a copy drift: the server may widen or narrow what a period may be, and a client that
 * inferred the blame would go on marking the box the old rule blamed.
 *
 * ⚠ IT KEYS ON `details.field` AND NOT ON A LOCAL LIST OF ERROR CODES, deliberately. A code
 * list here is the same second source of truth one level down — a third period refusal would
 * arrive naming its box and be silently unmarked until somebody remembered to extend the
 * list. The names are filtered against the boxes that EXIST instead, so a field this screen
 * does not render can never turn into a red control nobody can see.
 */
function blamedFields(query: PAQuery<PAStats>): ReadonlySet<string> {
  if (query.status !== 'error' || !(query.error instanceof ApiError)) return NO_FIELDS;
  const details = (query.error.details ?? {}) as { field?: unknown };
  if (typeof details.field !== 'string') return NO_FIELDS;
  const named = details.field
    .split(',')
    .map((name) => name.trim())
    .filter((name) => PERIOD_FIELDS.has(name));
  return named.length === 0 ? NO_FIELDS : new Set(named);
}

/** The answer: either «we cannot see the store», or the window, the totals and the series. */
function StatisticsData({ data }: { data: PAStats }) {
  const { t } = useTranslation();

  /*
    ⚠ THE FIRST BRANCH, AND IT RETURNS BEFORE ANY FIGURE EXISTS ON THE PAGE. Not «render the
    table with a warning above it» — the totals row alone would put five zeros on screen, and
    a subscriber reads «0 Б» as a fact about himself. There is nothing numeric below here.
  */
  if (!data.data_available) {
    return (
      <Card className="pa-block">
        <Alert type="warning" showIcon message={t('statistics.unavailableTitle')} />
        <p className="pa-field-hint">{t('statistics.unavailableText')}</p>
      </Card>
    );
  }

  /*
    ⚠ THE COLLECTION BOUNDARY, and it is NOT a verdict re-derived from something the server
    already decided — it is two published facts compared. `data_since` is a MEASUREMENT the
    server made (the first day it holds a row for this Account) and `from`/`to` is the window
    the server RESOLVED; which sentence a page carries is presentation, the same way the unit,
    the tab intersection and the gap sentence already are. What the portal must not do is GUESS
    the boundary, and it no longer has to.

    The comparison is lexicographic on `YYYY-MM-DD`, which is chronological for that format, and
    both dates are civil days in the OPERATOR's zone — so no `Date` is constructed and the
    browser's own zone never enters. Building one here is how «this month» came to be a
    different month one domain along.

    ⚠ `""` SHORT-CIRCUITS BOTH, and that is the whole handling of «unknown»: not a date, no
    sentence, no formatting.
  */
  const startsBeforeCollection = data.data_since !== '' && data.from < data.data_since;
  const endsBeforeCollection = data.data_since !== '' && data.to < data.data_since;

  /*
    ⚠ THE SECOND BRANCH THAT RETURNS BEFORE ANY FIGURE EXISTS, for the same reason as the first:
    the whole window predates the first row this store holds, so every total on the page is zero
    because of when WE started writing. «Всего трафика 0 Б» there is the same lie as the
    `data_available: false` zeros, one field further along.

    The `days.length` term is a guard against a body no server sends rather than a second
    condition: a window ending before the first stored row cannot contain rows, and if one ever
    arrived, hiding the subscriber's real traffic would be worse than printing a sentence beside
    it.
  */
  if (endsBeforeCollection && data.days.length === 0) {
    return (
      <Card className="pa-block">
        <Alert type="info" showIcon message={t('statistics.collectingTitle')} />
        <p className="pa-field-hint">
          {t('statistics.collectingText', { date: formatCivilDate(data.data_since) })}
        </p>
      </Card>
    );
  }

  // The intersection of what the server offers and what this build can fill. `tabs` is the
  // offer; `statsTabLabel` returns '' for a view with no table behind it.
  const drawn = data.tabs
    .map((tab) => ({ tab, label: statsTabLabel(tab, t) }))
    .filter((entry) => entry.label !== '');

  return (
    <>
      <Card className="pa-block" title={t('statistics.totalsTitle')} data-testid="pa-stats-totals">
        {/* ⚠ THE WINDOW THE SERVER RESOLVED, not the one the chips asked for. A preset is
            resolved in the OPERATOR's calendar — the browser does not know
            `general.billing_timezone` — so «месяц» is a pair of dates only the server can
            name, and printing the request back would be printing a guess. */}
        <p className="pa-page-lead">
          {t('statistics.window', {
            from: formatCivilDate(data.from),
            to: formatCivilDate(data.to),
          })}
        </p>
        {/* ⚠ THE PART OF THE WINDOW THAT PREDATES THE STORE'S FIRST ROW, said HERE and beside
            the figures it qualifies. The totals are the server's own sum over the whole window,
            and over a window we were only partly collecting for they are complete for the tail
            and empty for the head — a difference no figure on this card can show, and one the
            subscriber would otherwise read as his own consumption. */}
        {startsBeforeCollection ? (
          <p className="pa-field-hint">
            {t('statistics.collectingHint', { date: formatCivilDate(data.data_since) })}
          </p>
        ) : null}
        <FactList facts={totalsFacts(data, t)} />
      </Card>

      {data.tabs.length === 0 ? (
        <Alert type="info" showIcon message={t('statistics.noViews')} />
      ) : null}

      {drawn.map((entry) => (
        <Card className="pa-block" key={entry.tab} title={entry.label}>
          {entry.tab === STATS_TAB_DAYS ? (
            <>
              <DataTable
                columns={dayColumns(t)}
                rows={data.days}
                rowKey={(d) => d.day}
                /* ⚠ «трафика не зафиксировано» IS A CLAIM ABOUT THE SUBSCRIBER, so it is only
                   made about days that were being collected. Over a window whose head predates
                   `data_since` the same empty table means something weaker, and the sentence
                   says the weaker thing. */
                emptyKey={
                  startsBeforeCollection
                    ? 'statistics.daysEmptyCollecting'
                    : 'statistics.daysEmpty'
                }
              />
              {/* The gap rule, said once: a day with no traffic is absent and this screen does
                  not invent it. A reader counting rows would otherwise report missing days. */}
              <p className="pa-field-hint">{t('statistics.gapHint')}</p>
            </>
          ) : null}
        </Card>
      ))}
    </>
  );
}

function totalsFacts(
  data: PAStats,
  t: TFunction,
): Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> {
  return [
    {
      key: 'total',
      label: t('statistics.totalLabel'),
      value: <span className="pa-volume-strong">{volume(data.total_octets, t)}</span>,
      hint: t('statistics.totalHint'),
    },
    { key: 'in', label: t('statistics.inputLabel'), value: volume(data.total_input_octets, t) },
    { key: 'out', label: t('statistics.outputLabel'), value: volume(data.total_output_octets, t) },
    {
      key: 'sessions',
      label: t('statistics.sessionsLabel'),
      value: String(data.total_session_count),
    },
    {
      key: 'time',
      label: t('statistics.onlineLabel'),
      value: duration(data.total_session_seconds, t),
    },
  ];
}

function dayColumns(t: TFunction): ReadonlyArray<Column<PAStatsDay>> {
  return [
    { key: 'day', labelKey: 'statistics.columnDay', render: (d) => formatCivilDate(d.day) },
    {
      key: 'in',
      labelKey: 'statistics.columnInput',
      className: 'pa-td-right',
      render: (d) => volume(d.input_octets, t),
    },
    {
      key: 'out',
      labelKey: 'statistics.columnOutput',
      className: 'pa-td-right',
      render: (d) => volume(d.output_octets, t),
    },
    {
      key: 'total',
      labelKey: 'statistics.columnTotal',
      className: 'pa-td-right',
      render: (d) => volume(d.total_octets, t),
    },
    {
      key: 'sessions',
      labelKey: 'statistics.columnSessions',
      className: 'pa-td-right',
      render: (d) => String(d.session_count),
    },
    {
      key: 'time',
      labelKey: 'statistics.columnTime',
      render: (d) => duration(d.session_seconds, t),
    },
  ];
}

/**
 * volume renders one octet count as «8,5 ГБ» — the number from `format.ts`, the unit word from
 * the catalogue.
 *
 * ⚠ ONE TEXT NODE AND NOT TWO ELEMENTS, deliberately: an assertion on «5 ГБ» must match a
 * cell's whole text, and a figure split across two spans is one a test can only reach with a
 * regex — which is how a fixture whose values overlap as strings comes to pass an arm about
 * some other row.
 */
function volume(octets: number, t: TFunction): string {
  const parts = formatOctets(octets);
  if (parts === null) return NO_VALUE;
  return `${parts.value} ${t(parts.unitKey)}`;
}

/**
 * duration renders a count of seconds as «2 ч 12 мин», or as «12 мин» below an hour.
 *
 * Two wordings because «0 ч 12 мин» reads as a fault, and one wording for «less than a minute»
 * because «0 мин» beside a session count of 1 reads as though the session had not happened.
 */
function duration(seconds: number, t: TFunction): string {
  const parts = formatDuration(seconds);
  if (parts === null) return NO_VALUE;
  if (parts.hours > 0) return t('statistics.durationHours', parts);
  if (parts.minutes > 0) return t('statistics.durationMinutes', parts);
  return t('statistics.durationShort');
}
